import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";

// Public-profile color background gradients (mirrors backend PROFILE_COLOR_PALETTES)
const PUBLIC_BG_GRADIENTS = {
  red:    { from: "#1A0606", to: "#3A0F0F" },
  orange: { from: "#1F0F03", to: "#3A1A06" },
  yellow: { from: "#1F1A03", to: "#3A2F06" },
  green:  { from: "#031A0F", to: "#062F1A" },
  blue:   { from: "#06121F", to: "#0F1F3A" },
  purple: { from: "#160B22", to: "#2F0F3A" },
};
import {
  Loader2,
  Lock,
  Sparkles,
  Crown,
  Flame,
  Trophy,
  Calendar,
  Dumbbell,
  Instagram,
  Youtube,
  ExternalLink,
} from "lucide-react";
import Header from "@/components/Header";
import { SocialLinkRow } from "@/components/SocialLinks";
import HighlightReel from "@/components/HighlightReel";
import Footer from "@/components/Footer";
import ShareProfileModal from "@/components/ShareProfileModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicProfile() {
  const { handle } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);

  useEffect(() => {
    if (!handle) return;
    document.title = `@${handle} · OMNIMUSCLE`;
    // Add OG/Twitter card meta tags (best-effort — many crawlers prefer SSR but
    // most modern bots (Twitter, FB, Discord) execute light JS or read tags)
    const ogImage = `${API}/public/u/${handle}/og.png`;
    upsertMeta("og:title", `@${handle} · OMNIMUSCLE`);
    upsertMeta("og:image", ogImage);
    upsertMeta("og:url", `${window.location.origin}/u/${handle}`);
    upsertMeta("twitter:card", "summary_large_image");
    upsertMeta("twitter:image", ogImage);
    (async () => {
      try {
        const { data: d } = await axios.get(`${API}/public/u/${handle}`);
        setData(d);
      } catch (e) {
        if (e?.response?.status === 404) {
          setError(e?.response?.data?.detail || "Profile not found.");
        } else {
          setError("Couldn't load profile.");
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [handle]);

  if (loading) {
    return (
      <div className="App">
        <Header />
        <section className="pt-32 pb-20 flex items-center justify-center text-neutral-500 min-h-[50vh]">
          <Loader2 className="animate-spin mr-2 text-[#D4AF37]" size={18} />
          Loading profile…
        </section>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="App">
        <Header />
        <section className="pt-32 pb-20 max-w-xl mx-auto px-6 text-center min-h-[50vh]">
          <Lock size={42} className="mx-auto text-[#D4AF37] mb-3" />
          <h1 className="font-display text-3xl text-white mb-2">@{handle}</h1>
          <p className="text-neutral-400">{error}</p>
          <a href="/" className="btn-omni btn-outline-gold mt-6 inline-flex">
            ← Back to OmniMuscle
          </a>
        </section>
        <Footer />
      </div>
    );
  }

  const d = data || {};
  const streaks = d.streaks || {};
  const badges = d.badges || [];
  const pinnedCoach = d.pinned_coach;
  const pinnedWorkout = d.pinned_workout;
  const socials = d.socials || {};

  return (
    <div className="App">
      <Header />
      <main
        data-testid="public-profile"
        className="pt-28 pb-20 min-h-[80vh]"
        style={{
          background: PUBLIC_BG_GRADIENTS[d.profile_bg_color]
            ? `linear-gradient(to bottom, ${PUBLIC_BG_GRADIENTS[d.profile_bg_color].from}, ${PUBLIC_BG_GRADIENTS[d.profile_bg_color].to}, #0A0A0A)`
            : "#0A0A0A",
        }}
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header card */}
          <div className="bg-gradient-to-br from-[#1a0d2b] via-[#0F0F0F] to-[#0A0A0A] border-2 border-[#D4AF37] p-8 mb-6">
            <div className="flex flex-col sm:flex-row items-center sm:items-start gap-5">
              {d.picture ? (
                <img
                  src={d.picture}
                  alt={d.name}
                  className="w-24 h-24 sm:w-28 sm:h-28 object-contain bg-black border-4 border-[#D4AF37]"
                />
              ) : (
                <div className="w-24 h-24 sm:w-28 sm:h-28 bg-[#4C1D95] border-4 border-[#D4AF37] flex items-center justify-center font-display text-5xl text-[#D4AF37]">
                  {(d.name || "?").charAt(0).toUpperCase()}
                </div>
              )}
              <div className="flex-1 text-center sm:text-left">
                <div className="flex flex-wrap items-center justify-center sm:justify-start gap-2 mb-1">
                  {d.role === "owner" && (
                    <span className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold inline-flex items-center gap-1 border border-[#D4AF37] px-2 py-0.5">
                      <Crown size={10} /> Owner
                    </span>
                  )}
                  {d.is_premium && d.role !== "owner" && (
                    <span className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold inline-flex items-center gap-1 border border-[#D4AF37] px-2 py-0.5">
                      <Sparkles size={10} /> Premium
                    </span>
                  )}
                </div>
                <h1 className="font-display text-4xl text-white">{d.name}</h1>
                <p className="text-[#D4AF37] font-mono text-sm mt-1">@{d.handle}</p>
                {d.bio && (
                  <p className="text-neutral-300 text-sm mt-3 max-w-prose">{d.bio}</p>
                )}
                {(d.location || d.member_since) && (
                  <div className="flex flex-wrap items-center justify-center sm:justify-start gap-3 mt-3 text-[10px] uppercase tracking-widest text-neutral-500">
                    {d.location && <span>📍 {d.location}</span>}
                    {d.member_since && (
                      <span className="inline-flex items-center gap-1">
                        <Calendar size={10} /> Member since {d.member_since}
                      </span>
                    )}
                  </div>
                )}
                {(socials.instagram || socials.tiktok || socials.youtube || socials.facebook || socials.threads || socials.x || socials.discord) && (
                  <div className="mt-4">
                    <SocialLinkRow
                      source={{
                        social_instagram: socials.instagram,
                        social_tiktok: socials.tiktok,
                        social_youtube: socials.youtube,
                        social_facebook: socials.facebook,
                        social_threads: socials.threads,
                        social_x: socials.x,
                        social_discord: socials.discord,
                      }}
                      testIdPrefix="public-social"
                      className="justify-center sm:justify-start"
                    />
                  </div>
                )}
              </div>
              <button
                data-testid="public-share-btn"
                onClick={() => setShareOpen(true)}
                className="btn-omni btn-red"
              >
                <ExternalLink size={14} /> Share
              </button>
            </div>
          </div>

          {/* Streaks */}
          {d.streaks && (
            <div className="bg-[#0F0F0F] border border-zinc-800 p-5 mb-4">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold mb-3 flex items-center gap-2">
                <Flame size={11} /> Streaks
              </div>
              <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                {[
                  ["signin", "Sign-in"],
                  ["activity", "Activity"],
                  ["sleep", "Sleep"],
                  ["water", "Water"],
                  ["cardio", "Cardio"],
                  ["spin", "Spin"],
                ].map(([key, label]) => (
                  <div key={key} className="bg-[#0A0A0A] border border-zinc-800 p-2 text-center">
                    <div className="text-[9px] uppercase tracking-widest text-neutral-500">{label}</div>
                    <div className="font-display text-2xl text-white tabular-nums">
                      {streaks[key] || 0}<span className="text-xs text-[#D4AF37]">d</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Pinned coach plan */}
          {pinnedCoach && (
            <div
              data-testid="public-pinned-coach"
              className="bg-[#0F0F0F] border-2 border-[#D4AF37] p-5 mb-4"
            >
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold mb-1 flex items-center gap-2">
                <Sparkles size={11} /> AI Coach Plan
              </div>
              <h3 className="font-display text-2xl text-white mb-3">
                {pinnedCoach.goal ? `${pinnedCoach.goal}` : "My Plan"}
                {pinnedCoach.level && (
                  <span className="text-sm text-neutral-500 ml-2 font-sans">· {pinnedCoach.level}</span>
                )}
              </h3>
              <div
                className="ai-prose text-sm prose-invert max-w-none"
                dangerouslySetInnerHTML={{ __html: renderMd(pinnedCoach.plan || "") }}
              />
            </div>
          )}

          {/* Pinned workout */}
          {pinnedWorkout && (
            <div
              data-testid="public-pinned-workout"
              className="bg-[#0F0F0F] border-2 border-[#DC2626] p-5 mb-4"
            >
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#DC2626] font-bold mb-1 flex items-center gap-2">
                <Dumbbell size={11} /> Pinned Workout
              </div>
              <h3 className="font-display text-2xl text-white">{pinnedWorkout.title}</h3>
              {pinnedWorkout.tagline && (
                <p className="text-red-400 italic text-sm mt-1">"{pinnedWorkout.tagline}"</p>
              )}
              <div className="flex flex-wrap gap-2 mt-3 text-[10px] uppercase tracking-widest text-neutral-400">
                {pinnedWorkout.focus && (
                  <span className="border border-zinc-700 px-2 py-1">{pinnedWorkout.focus}</span>
                )}
                {pinnedWorkout.level && (
                  <span className="border border-zinc-700 px-2 py-1">{pinnedWorkout.level}</span>
                )}
                {pinnedWorkout.days_per_week && (
                  <span className="border border-zinc-700 px-2 py-1">
                    {pinnedWorkout.days_per_week} d/wk
                  </span>
                )}
                {pinnedWorkout.duration_min && (
                  <span className="border border-zinc-700 px-2 py-1">{pinnedWorkout.duration_min}m</span>
                )}
              </div>
            </div>
          )}

          {/* Badges */}
          {badges.length > 0 && (
            <div className="bg-[#0F0F0F] border border-zinc-800 p-5">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold mb-3 flex items-center gap-2">
                <Trophy size={11} /> Badges ({d.badge_count})
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-5 md:grid-cols-6 gap-2">
                {badges.map((b) => (
                  <div
                    key={b.id}
                    data-testid={`public-badge-${b.id}`}
                    title={b.title}
                    className="bg-[#0A0A0A] border border-zinc-800 p-3 text-center hover:border-[#D4AF37] transition-colors"
                  >
                    <div className="text-3xl leading-none mb-1">{b.icon}</div>
                    <div className="text-[10px] uppercase tracking-widest text-neutral-400 line-clamp-2">
                      {b.title}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <p className="text-center text-[10px] uppercase tracking-[0.3em] text-neutral-600 mt-8">
            Powered by OMNIMUSCLE 🐦‍🔥 · Holistic Healing · Flying Higher
          </p>
        </div>
        {/* 🏆 Highlight Reel */}
        <div className="mt-6">
          <HighlightReel handle={d.handle || handle} />
        </div>
      </main>
      <Footer />
      {shareOpen && (
        <ShareProfileModal
          handle={d.handle || handle}
          name={d.name}
          onClose={() => setShareOpen(false)}
        />
      )}
    </div>
  );
}

function socialUrl(kind, handle) {
  const h = (handle || "").replace(/^@/, "");
  if (kind === "instagram") return `https://instagram.com/${h}`;
  if (kind === "tiktok") return `https://tiktok.com/@${h}`;
  if (kind === "youtube") return `https://youtube.com/${h.startsWith("@") ? h : "@" + h}`;
  return "#";
}

function upsertMeta(prop, value) {
  if (!value) return;
  let tag = document.head.querySelector(`meta[property="${prop}"], meta[name="${prop}"]`);
  if (!tag) {
    tag = document.createElement("meta");
    if (prop.startsWith("og:")) tag.setAttribute("property", prop);
    else tag.setAttribute("name", prop);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", value);
}

// Minimal Markdown renderer
function renderMd(md) {
  if (!md) return "";
  let html = md
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/^### (.+)$/gm, '<h4>$1</h4>')
    .replace(/^## (.+)$/gm, '<h3>$1</h3>')
    .replace(/^# (.+)$/gm, '<h2>$1</h2>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/^- (.+)$/gm, '<li>$1</li>')
    .replace(/(<li>.*<\/li>(\n|$))+/g, (m) => `<ul>${m.replace(/\n/g, "")}</ul>`)
    .replace(/\n\n/g, '</p><p>')
    .replace(/\n/g, "<br/>");
  return `<p>${html}</p>`;
}
